/**
 * ESCHER FALLS — **今日の一面**（日替わり）を取ってくる。
 *
 * ## 何を、どこから
 * 面は毎朝スケジューラーが作り（tools/gen-daily.ts）、サイトの
 * `https://escher-falls.pages.dev/daily/YYYY-MM-DD.json` に置く。
 * **アプリの外から届くのは面の形（データ）だけで、プログラムは届かない。**
 * だから審査を通した版のまま、毎日新しい面を遊べる（審査ガイドライン 2.5.2 に触れない）。
 *
 * ## 通信が要るのはここだけ
 * この作品は通信なしで最後まで遊べる。日替わりだけが例外で、取れなければ
 * 「今日の一面」が開けないだけにする（本編・エクストラには一切響かない）。
 * 一度取れた面は端末に控えておき、同じ日のうちは通信なしでも遊び直せる。
 *
 * ## 体験版でも同じ URL を見る
 * PLiCy・itch.io の体験版は別のドメインで動くので、相対パスでは届かない。
 * 必ず絶対 URL を使う（Cloudflare Pages は静的ファイルに CORS を開けている）。
 */

import type { Level } from '../core/types';
import { parseLevel } from './levelLoader';

export const DAILY_BASE_URL = 'https://escher-falls.pages.dev/daily';

/** 日替わりの面の id の頭。`daily-YYYY-MM-DD`。 */
export const DAILY_PREFIX = 'daily-';

export function isDailyId(id: string): boolean {
  return id.startsWith(DAILY_PREFIX);
}

/** 端末の暦での今日（YYYY-MM-DD）。面は東京の日付で作るが、遊ぶ人の今日に合わせる。 */
export function todayKey(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * 受け取った面の控え。日付ごとに持つ（「これまでの一面」を通信なしで遊び直すため）。
 * **増え続けないよう、新しい順に {@link CACHE_LIMIT} 日ぶんだけ残す。**
 */
const CACHE_KEY = 'escher-falls/daily/v2';
const CACHE_LIMIT = 120;

function readAllCache(): Record<string, unknown> {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (raw === null) return {};
    const v = JSON.parse(raw) as unknown;
    return v !== null && typeof v === 'object' ? (v as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function readCache(date: string): unknown | null {
  return readAllCache()[date] ?? null;
}

function writeCache(date: string, level: unknown): void {
  try {
    const all = readAllCache();
    all[date] = level;
    const keep = Object.keys(all).sort().reverse().slice(0, CACHE_LIMIT);
    const trimmed: Record<string, unknown> = {};
    for (const k of keep) trimmed[k] = all[k];
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(trimmed));
  } catch {
    // 保存できなくても、次に開いたとき取り直すだけ。
  }
}

// =============================================================================
// これまでの一面
// =============================================================================

/**
 * 無料で遊べる「これまでの一面」の日数。**エクストラを買った人は全部。**
 *
 * 直近の数日だけを無料にするのは、見逃した人の取り返しを許しつつ、
 * 積み上がった過去の面を「エクストラを買う理由」にするため（2026-10-08 の判断・案A）。
 * 2026-10-09 に7日→3日へ縮めた。7日では見逃した人もほぼ無料で取り返せて、鍵に出会う人が少ないため。
 */
export const FREE_ARCHIVE_DAYS = 3;

/** `YYYY-MM-DD` の差（日数）。`a - b`。 */
export function daysBetween(a: string, b: string): number {
  const ms = Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`);
  return Math.round(ms / 86400000);
}

/** その日の面が、いま開いているか。 */
export function isArchiveOpen(date: string, today: string, extrasOwned: boolean): boolean {
  const age = daysBetween(today, date);
  // 先の日は、買った人に**明日の1面だけ**。毎日1面ずつ前もって開く（2026-10-09 の判断）。
  if (age < 0) return extrasOwned && age === -1;
  if (extrasOwned) return true;
  return age >= 1 && age <= FREE_ARCHIVE_DAYS;
}

const INDEX_CACHE_KEY = 'escher-falls/daily-index/v1';

/**
 * 公開済みの日付の一覧（index.json）を取る。取れなければ前に取った控え（それも無ければ null）。
 */
async function loadPublishedIndex(): Promise<unknown> {
  try {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 8000);
    const res = await fetch(`${DAILY_BASE_URL}/index.json`, { signal: controller.signal, cache: 'no-cache' });
    window.clearTimeout(timer);
    if (res.ok) {
      const raw: unknown = await res.json();
      try {
        window.localStorage.setItem(INDEX_CACHE_KEY, JSON.stringify(raw));
      } catch {
        // 控えが残せなくても、今回の一覧は使える。
      }
      return raw;
    }
  } catch {
    // 通信できない。下の控えに落ちる。
  }
  try {
    const raw = window.localStorage.getItem(INDEX_CACHE_KEY);
    return raw === null ? null : (JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

function pickDates(raw: unknown, keep: (date: string) => boolean): string[] {
  const days = (raw as { days?: Array<{ date?: unknown }> } | null)?.days;
  if (!Array.isArray(days)) return [];
  return days
    .map((d) => d.date)
    .filter((d): d is string => typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d) && keep(d))
    .sort();
}

/**
 * 公開済みの日付の一覧を取る。**今日より前の日だけ**を、新しい順に返す。
 * 取れなければ、前に取った一覧の控えを返す（それも無ければ空）。
 */
export async function fetchArchiveDates(today: string = todayKey()): Promise<string[]> {
  return pickDates(await loadPublishedIndex(), (d) => d < today).reverse();
}

/**
 * 公開済みの**先の日**（今日より後）の日付を、近い順に返す。
 * 毎週まとめて先の2週間ぶんを公開しているが、エクストラを買った人に見せるのは明日の1面だけ（isArchiveOpen）。
 * **無料の人には使わない**（呼ぶ側 main が所持を見て空にする）。
 */
export async function fetchUpcomingDates(today: string = todayKey()): Promise<string[]> {
  return pickDates(await loadPublishedIndex(), (d) => d > today);
}

/**
 * その日の面を取ってくる。**決して例外を投げない。** 取れなければ null。
 *
 * 届いたデータは本編と同じ `parseLevel` に通す。形が壊れていたら弾く——
 * 外から来たものを検査せずに盤面へ渡さない。
 */
export async function fetchDaily(date: string = todayKey()): Promise<Level | null> {
  const parse = (raw: unknown): Level | null => {
    try {
      const level = parseLevel(raw, `${DAILY_PREFIX}${date}`);
      return isDailyId(level.id) ? level : null;
    } catch {
      return null;
    }
  };

  const cached = readCache(date);
  if (cached !== null) {
    const level = parse(cached);
    if (level !== null) return level;
  }

  try {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 8000);
    const res = await fetch(`${DAILY_BASE_URL}/${date}.json`, {
      signal: controller.signal,
      cache: 'no-cache',
    });
    window.clearTimeout(timer);
    if (!res.ok) return null;
    const raw: unknown = await res.json();
    const level = parse(raw);
    if (level !== null) writeCache(date, raw);
    return level;
  } catch {
    return null;
  }
}
